import type { CreateManufacturerDto, SerializedManufacturer, UpdateManufacturerDto } from '@enmon/commons/lib/tools';
import { BASE_REST_API_URL } from 'common/constants';
import type { StrapiApiResponse } from 'api/types/strapiApiResponse';
import type { BaseAPIParams } from 'lib/utils/BaseAPIParams';
import { buildQuery, type Filters } from '@enmon/commons/lib/tools/utils/buildQuery';
import type { Commodity } from '@enmon/commons/lib/shared';
import { BaseApiClient } from './baseApiClient';

export type ManufacturersApiFilter = BaseAPIParams<SerializedManufacturer> & {
  search?: string;
  commodity?: Commodity;
};

export type GetManufacturersArg = {
  filter: ManufacturersApiFilter;
  signal: AbortSignal | undefined;
};
export type GetOneManufacturerArg = {
  manufacturerId: SerializedManufacturer['id'];
  signal: AbortSignal | undefined;
};

export type GetManufacturersResponse = StrapiApiResponse<SerializedManufacturer>;

export type CreateManufacturerArg = CreateManufacturerDto;
export type CreateManufacturerResult = SerializedManufacturer;

export type UpdateManufacturerArg = {
  id: SerializedManufacturer['id'];
  values: UpdateManufacturerDto;
};
export type UpdateManufacturerResult = SerializedManufacturer;

export type DeleteManufacturerArg = SerializedManufacturer['id'];

export class ManufacturersApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('manufacturers', BASE_REST_API_URL, jwt);
  }

  public async findAll({ filter, signal }: GetManufacturersArg): Promise<GetManufacturersResponse> {
    const { commodity, search, populate, page, pageSize, limit, start, sort, fields } = filter ?? {};

    const response = await this.axios.get<GetManufacturersResponse>('', {
      params: buildQuery<SerializedManufacturer>({
        filters: {
          ...(search && { name: { $containsi: search } }),
          ...(commodity && { meter_models: { commodity: { $eq: commodity } } }),
        } as Filters<SerializedManufacturer>,
        fields,
        populate,
        pagination: {
          page,
          pageSize,
          limit,
          start,
        },
        sort,
      }),
      signal,
    });

    return response.data;
  }

  public async findOne({ manufacturerId, signal }: GetOneManufacturerArg): Promise<SerializedManufacturer> {
    const response = await this.axios.get<{ data: SerializedManufacturer }>(`${manufacturerId}`, { signal });

    return response.data.data;
  }

  public async create(values: CreateManufacturerArg): Promise<CreateManufacturerResult> {
    const response = await this.axios.post<{ data: SerializedManufacturer }>('', { data: values });

    return response.data.data;
  }

  public async update({ id, values }: UpdateManufacturerArg): Promise<UpdateManufacturerResult> {
    const response = await this.axios.put<{ data: SerializedManufacturer }>(`${id}`, { data: values });

    return response.data.data;
  }

  public async delete(id: DeleteManufacturerArg): Promise<SerializedManufacturer> {
    const response = await this.axios.delete<SerializedManufacturer>(`${id}`);

    return response.data;
  }
}
